import React, { useEffect, useRef, useState } from 'react';
import api from '../lib/api';
import { Loader2, Check, AlertTriangle, X, Smartphone } from 'lucide-react';
import { getBootLocale } from '../i18n';

/**
 * BitPurchaseModal — drives a SaaS-billing payment via BIT/GamaPay.
 *
 * Flow:
 *  1. POST /billing/bit/initiate with { kind, ...packageBody, payerPhone }
 *     → sessionId + walletSchemes + smsSent
 *  2. Show "SMS sent" hint + (mobile only) "Open in Bit" deep-link buttons.
 *  3. Poll GET /billing/bit/status/:sessionId every 3s until completed/failed
 *     or 10min timeout.
 *  4. On completed → onSuccess() then close.
 *
 * The user's phone is required (it's where the Bit SMS goes). We default
 * to org.phone but let the user override per-purchase.
 */

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess?: (data?: any) => void;
  defaultPhone?: string;
  defaultIdNumber?: string;
  // The initiate body — kind + per-kind fields. The component appends the
  // phone (under whatever phoneFieldName is set) before sending.
  initiateBody: Record<string, any> | null;
  title?: string;
  // Endpoints + field name. Defaults match the SaaS billing flow; pass
  // payments.* endpoints for public donations.
  initiateUrl?: string;     // default '/billing/bit/initiate'
  statusUrl?: string;       // default '/billing/bit/status/'   (sessionId is appended)
  phoneFieldName?: string;  // default 'payerPhone'  (donations use 'donorPhone')
  // Donation flow: show an optional 9-digit Israeli ID input. The value
  // is sent under `idFieldName` (default 'donorIdNumber') so the
  // /payments/bit/initiate handler can stash it on the session and
  // back-link it to the Contact card on finalize. Hidden by default
  // (SaaS billing doesn't need it).
  showIdField?: boolean;
  idFieldName?: string;
}

const BitPurchaseModal: React.FC<Props> = ({
  open,
  onClose,
  onSuccess,
  defaultPhone = '',
  defaultIdNumber = '',
  initiateBody,
  title = 'תשלום ב-Bit',
  initiateUrl = '/billing/bit/initiate',
  statusUrl = '/billing/bit/status/',
  phoneFieldName = 'payerPhone',
  showIdField = false,
  idFieldName = 'donorIdNumber',
}) => {
  const [phone, setPhone] = useState(defaultPhone);
  const [idNumber, setIdNumber] = useState(defaultIdNumber);
  const [stage, setStage] = useState<'phone' | 'sent' | 'success' | 'failed' | 'error'>('phone');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [schemes, setSchemes] = useState<{ ios: string | null; android: string | null }>({ ios: null, android: null });
  const [breakdown, setBreakdown] = useState<{ net: number; vat: number; gross: number; description: string } | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (open) {
      setPhone(defaultPhone);
      setIdNumber(defaultIdNumber);
      setStage('phone');
      setErrorMsg('');
      setSessionId(null);
      setSchemes({ ios: null, android: null });
      setBreakdown(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const send = async () => {
    if (!initiateBody) return;
    const cleanPhone = (phone || '').replace(/\s+/g, '');
    if (!/^05\d{8}$/.test(cleanPhone)) {
      setErrorMsg('פורמט טלפון לא תקין (לדוגמה 0501234567)');
      return;
    }
    // When showIdField is on (donation flow), ת.ז. is mandatory — required
    // both for issuing a valid receipt and for matching the donor to the
    // Contact card. SaaS billing leaves showIdField=false and is unaffected.
    const cleanId = (idNumber || '').replace(/\D/g, '');
    if (showIdField) {
      if (!cleanId) {
        setErrorMsg('יש להזין תעודת זהות לפני שליחת SMS');
        return;
      }
      if (!/^\d{9}$/.test(cleanId)) {
        setErrorMsg('ת״ז חייבת להיות 9 ספרות');
        return;
      }
    }
    setSubmitting(true);
    setErrorMsg('');
    try {
      const r = await api.post(initiateUrl, {
        ...initiateBody,
        [phoneFieldName]: cleanPhone,
        ...(showIdField ? { [idFieldName]: cleanId } : {}),
      });
      const data = r.data || {};
      setSessionId(data.sessionId);
      setSchemes({ ios: data.walletSchemeIos || null, android: data.walletSchemeAndroid || null });
      setBreakdown({
        net: Number(data.net) || 0,
        vat: Number(data.vat) || 0,
        gross: Number(data.gross) || 0,
        description: data.description || '',
      });
      setStage('sent');
      startPolling(data.sessionId);
    } catch (e: any) {
      setStage('error');
      setErrorMsg(e.response?.data?.error || 'שגיאה ביצירת חיוב Bit');
    } finally {
      setSubmitting(false);
    }
  };

  const startPolling = (sid: string) => {
    if (pollRef.current) clearInterval(pollRef.current);
    const startedAt = Date.now();
    pollRef.current = setInterval(async () => {
      // 10 minute hard cap.
      if (Date.now() - startedAt > 10 * 60 * 1000) {
        if (pollRef.current) clearInterval(pollRef.current);
        return;
      }
      try {
        const r = await api.get(`${statusUrl}${sid}`);
        const status = r.data?.status;
        if (status === 'completed') {
          if (pollRef.current) clearInterval(pollRef.current);
          setStage('success');
          if (onSuccess) onSuccess(r.data);
        } else if (status === 'expired' || status === 'failed') {
          if (pollRef.current) clearInterval(pollRef.current);
          setStage('failed');
        }
      } catch { /* keep polling on transient errors */ }
    }, 3000);
  };

  const isMobile = typeof navigator !== 'undefined' && /Android|iPhone|iPad/i.test(navigator.userAgent);
  const deepLink = isMobile ? (/(iPhone|iPad)/i.test(navigator.userAgent) ? schemes.ios : schemes.android) : null;

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" dir={getBootLocale() === 'en' ? 'ltr' : 'rtl'}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-3 left-3 text-gray-400 hover:text-gray-700"
          aria-label="סגור"
        >
          <X size={20} />
        </button>
        <div className="flex items-center gap-2 mb-1">
          <Smartphone size={20} className="text-pink-500" />
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
        </div>
        <p className="text-xs text-gray-500 mb-4">תשלום מהיר באפליקציית Bit — ללא הזנת כרטיס אשראי.</p>

        {breakdown && (
          <div className="bg-gray-50 border rounded-lg p-3 mb-4 text-sm space-y-1">
            <p className="font-semibold text-gray-800">{breakdown.description}</p>
            <p className="text-gray-500">
              נטו ₪{breakdown.net} + מע״מ ₪{breakdown.vat} = <strong>₪{breakdown.gross}</strong>
            </p>
          </div>
        )}

        {stage === 'phone' && (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-1">טלפון לקבלת SMS עם קישור Bit</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="0501234567"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
              dir="ltr"
            />
            {showIdField && (
              <>
                <label className="block text-sm font-medium text-gray-700 mt-3 mb-1">
                  תעודת זהות <span className="text-red-500">*</span> <span className="text-gray-400 font-normal text-xs">(לקבלה ולקישור לכרטיס שלך)</span>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={9}
                  value={idNumber}
                  onChange={(e) => setIdNumber(e.target.value.replace(/\D/g, ''))}
                  placeholder="9 ספרות"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                  dir="ltr"
                />
              </>
            )}
            {errorMsg && (
              <p className="mt-2 text-xs text-red-600 flex items-center gap-1">
                <AlertTriangle size={12} /> {errorMsg}
              </p>
            )}
            <button
              onClick={send}
              disabled={submitting}
              className="mt-4 w-full bg-pink-600 hover:bg-pink-700 text-white rounded-lg py-2.5 text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting && <Loader2 size={14} className="animate-spin" />}
              שלח קישור ב-SMS
            </button>
          </>
        )}

        {stage === 'sent' && (
          <div className="space-y-3">
            <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
              <Loader2 size={16} className="animate-spin text-blue-600 mt-0.5 shrink-0" />
              <div className="text-sm text-blue-900">
                <p className="font-semibold">נשלח SMS אל {phone}</p>
                <p className="text-xs">פתח את הקישור באפליקציית Bit ואשר את התשלום. לאחר אישור החבילה תזוכה אוטומטית.</p>
              </div>
            </div>

            {deepLink && (
              <a
                href={deepLink}
                className="block w-full bg-pink-600 hover:bg-pink-700 text-white rounded-lg py-2.5 text-sm font-semibold text-center"
              >
                פתח באפליקציית Bit
              </a>
            )}

            <p className="text-xs text-gray-400 text-center">החלון יסגר אוטומטית כשהתשלום יתאשר.</p>
          </div>
        )}

        {stage === 'success' && (
          <div className="text-center py-6">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-green-100 mb-3">
              <Check size={28} className="text-green-600" />
            </div>
            <p className="font-bold text-gray-900">החבילה נרכשה בהצלחה</p>
            <p className="text-sm text-gray-500 mt-1">היתרה תתעדכן תוך מספר שניות.</p>
            <button
              onClick={onClose}
              className="mt-4 w-full bg-gray-900 hover:bg-gray-800 text-white rounded-lg py-2 text-sm font-semibold"
            >
              סגור
            </button>
          </div>
        )}

        {stage === 'failed' && (
          <div className="text-center py-6">
            <AlertTriangle size={36} className="text-red-500 mx-auto mb-2" />
            <p className="font-bold text-gray-900">התשלום לא הושלם</p>
            <p className="text-sm text-gray-500 mt-1">ניתן לנסות שוב.</p>
            <button
              onClick={() => setStage('phone')}
              className="mt-4 w-full bg-pink-600 hover:bg-pink-700 text-white rounded-lg py-2 text-sm font-semibold"
            >
              נסה שוב
            </button>
          </div>
        )}

        {stage === 'error' && (
          <div className="text-center py-6">
            <AlertTriangle size={36} className="text-red-500 mx-auto mb-2" />
            <p className="font-bold text-gray-900">שגיאה</p>
            <p className="text-sm text-gray-600 mt-1">{errorMsg}</p>
            <button
              onClick={() => setStage('phone')}
              className="mt-4 w-full bg-gray-900 hover:bg-gray-800 text-white rounded-lg py-2 text-sm font-semibold"
            >
              חזור
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BitPurchaseModal;
